import React from "react";
import styled from "styled-components";
import { LoadingContainer, LoadingText, Spinner } from "@/shared/components/Loading/Loading";
import { mediaPhone } from "@/shared/theme/breakpoints";

/*
 * The card around the views/engagements chart: title, note, and the loading and empty states. The
 * desktop chart and the phone weekly chart both render inside it, so the card looks the same.
 */

const ChartCard = styled.div`
	background: #ffffff;
	border-radius: 12px;
	padding: 24px;
	width: 100%;

	${mediaPhone`
		padding: 16px;
		border-radius: 16px;
	`}
`;

const ChartHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
`;

const ChartTitle = styled.h3`
	font-family: "Poppins";
	font-weight: 700;
	font-size: 18px;
	color: #222222;
	margin: 0;

	${mediaPhone`
		font-size: 16px;
	`}
`;

const InfoNote = styled.p`
	font-family: "Poppins";
	font-size: 12px;
	color: #969696;
	margin: 0 0 0 0;
`;

const EmptyChart = styled.div`
	height: 350px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: "Poppins";
	font-size: 14px;
	color: #969696;
`;

const ChartLoading = styled(LoadingContainer)`
	height: 350px;

	${mediaPhone`
		height: 260px;
	`}
`;

/** The chart's drawing area; it sets the canvas height. */
export const ChartArea = styled.div`
	height: 350px;

	${mediaPhone`
		height: 260px;
		margin-top: 8px;
	`}
`;

export type ChartPlatform = "all" | "tiktok" | "instagram" | "facebook" | "youtube";

interface TimeseriesChartFrameProps {
	platform: ChartPlatform;
	/** What the plotted points are, e.g. "Observed growth days only". */
	note: string;
	timezone: string;
	loading: boolean;
	hasData: boolean;
	/** Controls under the note, shown once there is data (the phone chart's Monthly / Weekly toggle). */
	toolbar?: React.ReactNode;
	children: React.ReactNode;
}

export const TimeseriesChartFrame: React.FC<TimeseriesChartFrameProps> = ({
	platform,
	note,
	timezone,
	loading,
	hasData,
	toolbar,
	children,
}) => (
	<ChartCard>
		<ChartHeader>
			<ChartTitle>Views and Engagement Over Time</ChartTitle>
		</ChartHeader>
		<InfoNote>
			{platform === "all"
				? "All platform videos"
				: `${platform.charAt(0).toUpperCase() + platform.slice(1)} videos`}
			{" · "}
			{note} ({timezone})
		</InfoNote>
		{loading ? (
			<ChartLoading role="status" aria-live="polite">
				<Spinner />
				<LoadingText>Loading chart…</LoadingText>
			</ChartLoading>
		) : hasData ? (
			<>
				{toolbar}
				{children}
			</>
		) : (
			<EmptyChart>No observed metrics for this platform and period.</EmptyChart>
		)}
	</ChartCard>
);
